
import appStore from "@/utils/store";
import { requestSE } from "../request";
import { getUserBackground } from "../orbbyte";
import { CalooAdminConfig } from "../admin";

// const BASE_URL = 'http://localhost:5001/meyo-230113/us-central1/apiOorenji';
const BASE_URL = CalooAdminConfig?.googleApi || import.meta.env.VITE_MEEFOD_API_URL;


export async function activeCampaignCalooAction(uid: string, action: string) {
    try {
        const response = await requestSE(uid).get(
            `${BASE_URL}/activeCampaignCalooAction/${action}`);
        return response.data;
    } catch (error) {
        console.error(`❌ Error in activeCampaignCalooAction:`, error);
        return "error";
    }
}

// ----------------------------------------------------------------
// USER REQUIREMENTS MANAGEMENT
export async function getUserRequirementsSE(user) {
    try {
        const response = await requestSE(user.firebaseUser).post(
            `${BASE_URL}/v3/get/dailyNutrition`,
            {
                uid: user.firebaseUser ? user.firebaseUser : user.uid,
                idPost: user.idPost,
                isCaloo: true,
            }
        );
        console.log("User requirements response:", response.data);
        return response.data;
    } catch (error) {
        return "error";
    }
}
export function mapUserRequirements(rawRequirements) {
    const settings = appStore.get("settingsCaloo");
    const userPhenotype = appStore.get("userPhenotype");
    const useBasalCalories = settings?.healthIntegration || (!settings?.healthIntegration && !settings?.estimateCalories);
    const activity = userPhenotype?.activity;
    const PALs = [1.05, 1.375, 1.55, 1.725];
    const pal = PALs[activity] ?? 1.375;
    const factor = useBasalCalories ? 1 / pal : 1;

    const scale = (value) =>
        value != null ? value * factor : null;

    // Macronutrientes que queremos escalar
    const macronutrients = {
        kcal:
            scale(
                rawRequirements.kcalENxJonesFactorWithFibers ??
                rawRequirements.kcalEREU_11692011 ??
                0
            ),
        protein: scale(rawRequirements.protein),
        fat: scale(rawRequirements.fat),
        carbs: scale(rawRequirements.carbohydrate),
        fatSaturated: scale(rawRequirements.fASaturated),
        fatMono: scale(rawRequirements.fAMono),
        fatPoly: scale(rawRequirements.fAPoly),
        omega3: scale(rawRequirements.omega3),
        omega6: scale(rawRequirements.omega6),
        cholesterol: scale(rawRequirements.cholesterol),
    };

    // Macronutrientes que NO queremos escalar
    const nonScaled = {
        fiber: rawRequirements.fibres ?? null,
        sugars: rawRequirements.sugars ?? null,
    };

    // Micronutrientes
    const micronutrients = {
        calcium: rawRequirements.calcium?.value ?? null,
        iron: rawRequirements.iron?.value ?? null,
        magnesium: rawRequirements.magnesium?.value ?? null,
        potassium: rawRequirements.potassium?.value ?? null,
        vitaminD: rawRequirements.vitaminD?.value ?? null,
        vitaminC: rawRequirements.vitaminC?.value ?? null,
        vitaminB12: rawRequirements.vitaminB12?.value ?? null,
        folate: rawRequirements.folate?.value ?? null,
    };

    return {
        ...macronutrients,
        ...nonScaled,
        ...micronutrients,
        ...rawRequirements?.other,
    };
}
export async function getUserRequirements(forceCalculate = false) {
    console.log("* GetUserRequirements called with forceCalculate:", forceCalculate);
    // Intenta obtener los requirements del store
    let requirements = appStore.get("requirements");
    if (!forceCalculate && requirements && requirements !== "error " && requirements.kcal !== 0 && requirements.kcal !== null) {
        // console.log("✅ User requirements found in store:", requirements);
        return requirements;
    }
    // No están en store, obtenemos el usuario
    try {
        // Llama al servicio externo
        const raw = await getUserRequirementsSE(appStore.get("user"));
        if (!raw) {
            console.error("No requirements received from server");
            return null;
        }
        const mapped = mapUserRequirements(raw);
        appStore.set("requirements", mapped);
        console.log("✅ User requirements fetched and stored:", mapped);
        return mapped;
    } catch (err) {
        console.error("Error fetching user requirements:", err);
        return null;
    }
}
// ----------------------------------------------------------------
// USER BACKGROUND MANAGEMENT --> PHENOTYPE
export async function getUserBackgroundSE(forceCalculate = false) {
    try {
        let background = appStore.get("userPhenotype");
        if (background && background !== "error " && !forceCalculate) {
            // console.log("✅ User background found in store:", background);
            return background;
        }
        const userBkg = (await getUserBackground({ idPost: appStore.get("user").idPost }))?.data[0] || {};
        // console.log("📥 User background fetched:", JSON.stringify(userBkg));
        return mapAndStoreUserPhenotype(userBkg);
    } catch (error) {
        console.error("Error fetching user background:", error);
        return null;
    }
}

export function mapAndStoreUserPhenotype(rawPhenotype) {
    if (!rawPhenotype) {
        console.error("No phenotype data provided");
        return;
    }
    const age = rawPhenotype.birthdate
        ? calculateAge(rawPhenotype.birthdate)
        : null;

    const mappedPhenotype = {
        idPost: rawPhenotype.idPost,
        gender: rawPhenotype.gender,
        birthdate: rawPhenotype.birthdate,
        age,
        height: rawPhenotype.height ? parseFloat(rawPhenotype.height) : null,
        weight: rawPhenotype.weight ? parseFloat(rawPhenotype.weight) : null,
        activityType: rawPhenotype.activityType,
        pregnant: rawPhenotype.pregnant === "true",
        breastfeeding: rawPhenotype.breastfeeding === "true",
        menopause: rawPhenotype.menopause === "true",
        allergens: Array.isArray(rawPhenotype.allergens) ? rawPhenotype.allergens.flat() : [],
        ingredientsToAvoid: Array.isArray(rawPhenotype.ingredientsToAvoid) ? rawPhenotype.ingredientsToAvoid.flat() : [],
        foodCategoriesToAvoid: Array.isArray(rawPhenotype.foodCategoriesToAvoid) ? rawPhenotype.foodCategoriesToAvoid.flat() : [],
        dietType: rawPhenotype.dietType,
        goal: rawPhenotype.goal,
        plan: rawPhenotype.plan,
        preparationTime: rawPhenotype.preparationTime ? parseInt(rawPhenotype.preparationTime) : null,
        mealSchedule: rawPhenotype.mealSchedule || [],
        workoutSchedule: rawPhenotype.workoutSchedule || []
    };

    appStore.set("userPhenotype", mappedPhenotype);
    console.log("✅ User phenotype stored:", mappedPhenotype);
    return mappedPhenotype;
}

/**
 * Calcula la edad a partir de la fecha de nacimiento en formato YYYY-MM-DD
 * @param {string} birthdate
 */
function calculateAge(birthdate) {
    const today = new Date();
    const birth = new Date(birthdate);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
        age--;
    }
    return age;
}

export async function getKcalGoal() {
    let requirements = appStore.get("requirements");

    if (!requirements) {
        requirements = await getUserRequirements();
    }

    // Intenta obtener kcal del campo más fiable
    const kcal = requirements.kcalENxJonesFactorWithFibers
        || requirements.kcalEREU_11692011
        || requirements.kcal
        || null;

    return kcal;
}